import { useLanguage } from "@/context/language"
import { createUniqueId, For, Show } from "solid-js"
import { createStore } from "solid-js/store"

const useCases = ["newCompany", "handover", "sales", "support", "operations", "people", "projects", "management"] as const

export function KnowledgeGuidance() {
  const language = useLanguage()
  const id = createUniqueId()
  const [state, setState] = createStore<{ selected: (typeof useCases)[number] | "" }>({ selected: "" })

  return (
    <section class="border-b border-v2-border-border-base bg-v2-background-bg-layer-01 px-5 py-4" aria-labelledby={`${id}-title`}>
      <h3 id={`${id}-title`} class="text-[14px] font-semibold">{language.t("knowledge.guidance.title")}</h3>
      <p class="mt-1 max-w-2xl text-[12px] leading-5 text-v2-text-text-muted">{language.t("knowledge.guidance.description")}</p>
      <label for={id} class="mt-3 block text-[11px] font-medium">{language.t("knowledge.guidance.useCase")}</label>
      <select
        id={id}
        value={state.selected}
        class="mt-1 min-h-11 w-full max-w-md rounded-lg border border-v2-border-border-base bg-v2-background-bg-base px-3 py-2 text-[12px] focus-visible:outline-2 focus-visible:outline-offset-2"
        onChange={(event) => {
          const selected = useCases.find((item) => item === event.currentTarget.value)
          setState("selected", selected ?? "")
        }}
      >
        <option value="">{language.t("knowledge.guidance.choose")}</option>
        <For each={useCases}>{(item) => <option value={item}>{language.t(`knowledge.guidance.${item}.title`)}</option>}</For>
      </select>
      <div aria-live="polite">
        <Show when={state.selected}>
          {(selected) => (
            <div class="mt-4 space-y-4 text-[12px] leading-5">
              <div class="grid items-start gap-3 lg:grid-cols-2">
                <For each={["references", "inputs"] as const}>
                  {(group) => (
                    <section class="min-w-0 rounded-xl border border-v2-border-border-base bg-v2-background-bg-base p-4" aria-labelledby={`${id}-${group}`}>
                      <h4 id={`${id}-${group}`} class="text-[13px] font-semibold">{language.t(`knowledge.guidance.${group}`)}</h4>
                      <p class="mt-1 text-[11px] leading-5 text-v2-text-text-muted">{language.t(`knowledge.guidance.${group}Description`)}</p>
                      <ul class="mt-3 list-disc space-y-2 border-t border-v2-border-border-muted pt-3 pl-4 marker:text-v2-text-text-muted">
                        <For each={language.t(`knowledge.guidance.${selected()}.${group}`).split("\n")}>
                          {(item) => <li class="pl-1 break-words">{item}</li>}
                        </For>
                      </ul>
                    </section>
                  )}
                </For>
              </div>
              <Show when={selected() === "newCompany" || selected() === "handover"}>
                <aside class="space-y-2 rounded-lg border border-v2-border-border-muted p-4 text-[11px] leading-5 text-v2-text-text-muted" aria-labelledby={`${id}-notes`}>
                  <h4 id={`${id}-notes`} class="font-semibold text-v2-text-text-base">{language.t("knowledge.guidance.notes")}</h4>
                  <p>{language.t("knowledge.guidance.companyFacts")}</p>
                  <Show when={selected() === "newCompany"}>
                    <p>{language.t("knowledge.guidance.capital")}</p>
                  </Show>
                  <Show when={selected() === "handover"}>
                    <p>{language.t("knowledge.guidance.cutoff")}</p>
                  </Show>
                </aside>
              </Show>
            </div>
          )}
        </Show>
      </div>
    </section>
  )
}
